import { useCallback, useMemo } from 'react';
import styled from '@emotion/styled';
import type { ActionMeta, GroupBase, MultiValue, OptionProps } from 'react-select';
import Select, { components as ReactSelectComponents } from 'react-select';
import { useTheme } from '@trinity/chat-components/components/Theme';
import { ChatContextType } from '@trinity/chat';
import type { BrandInsightsInstanceContext } from '@trinity/chat';
import type { DataSourceFilter } from '@trinity/chat';
import * as Presentation from './Presentation';

type Props = {
  context: BrandInsightsInstanceContext.ItemWithMetadata;
  readOnly?: boolean;
  options: DataSourceFilter.Item[];
  value: DataSourceFilter.Item[];
  onChange: (value: DataSourceFilter.Item[], meta: ActionMeta<DataSourceFilter.Item>) => unknown;
  dataSources?: { id: number; name: string; }[];
};

export function DataSource({ context, options, readOnly, value, onChange, dataSources }: Props) {
  const theme = useTheme();

  const isAgentContext = context?.type === ChatContextType.Agent;

  const transformedDataSources = useMemo(() => {
    if (!dataSources) return undefined;
    return dataSources.map(ds => ({
      id: String(ds.id),
      name: ds.name,
    }));
  }, [dataSources]);

  const availableOptions = useMemo(() => {
    return isAgentContext && transformedDataSources ? transformedDataSources : options;
  }, [isAgentContext, transformedDataSources, options]);

  const selectedDataSources = useMemo(() => {
    return value || [];
  }, [value]);

  const unselectedOptions = useMemo(() => {
    const selectedIds = new Set(selectedDataSources.map(ds => ds.id));
    return availableOptions.filter(option => !selectedIds.has(option.id));
  }, [availableOptions, selectedDataSources]);

  const selectStyles = useMemo(() => Presentation.buildSelectStyles(theme), [theme]);
  const selectTheme = useMemo(() => Presentation.buildSelectTheme(theme), [theme]);


  const handleToggleDataSource = useCallback((dataSource: DataSourceFilter.Item, isSelected: boolean) => {
    if (readOnly) return;

    let newValue: DataSourceFilter.Item[];
    let meta: ActionMeta<DataSourceFilter.Item>;

    if (isSelected) {
      newValue = selectedDataSources.filter(ds => ds.id !== dataSource.id);
      meta = { action: 'remove-value', removedValue: dataSource };
    } else {
      newValue = [...selectedDataSources, dataSource];
      meta = { action: 'select-option', option: dataSource };
    }

    onChange(newValue, meta);
  }, [selectedDataSources, onChange, readOnly]);

  const handleMultiSelectChange = useCallback((newValue: MultiValue<DataSourceFilter.Item>, meta: ActionMeta<DataSourceFilter.Item>) => {
    onChange(Array.from(newValue), meta);
  }, [onChange]);

  const handleNoOptionsMessage = useCallback((obj: { inputValue: string }) => {
    if (unselectedOptions.length === 0) return 'All sources selected';
    return obj.inputValue?.length ? `No source found` : null;
  }, [unselectedOptions.length]);

  const hasDataSources = availableOptions.length > 0;

  return (
    <Presentation.Row>
      <Presentation.Label>Data Source</Presentation.Label>
      <Presentation.Value>
        <DataSourceContainer>
          {hasDataSources && (
            <Select<DataSourceFilter.Item, true>
              styles={selectStyles}
              components={{
                Option: (props) => <MenuOption {...props} onToggle={handleToggleDataSource} selectedIds={selectedDataSources.map(ds => ds.id)} />,
              }}
              isDisabled={readOnly}
              theme={selectTheme}
              value={selectedDataSources}
              isMulti
              options={availableOptions}
              closeMenuOnSelect={false}
              hideSelectedOptions={false}
              isSearchable={false}
              isClearable={false}
              placeholder="Select data sources..."
              noOptionsMessage={handleNoOptionsMessage}
              getOptionLabel={o => o.name}
              getOptionValue={o => o.id}
              onChange={handleMultiSelectChange}
              menuPlacement="auto"
            />
          )}

          {availableOptions.length === 0 && (
            <EmptyText>No data sources available</EmptyText>
          )}
        </DataSourceContainer>
      </Presentation.Value>
    </Presentation.Row>
  );
}

function MenuOption(props: OptionProps<DataSourceFilter.Item, true, GroupBase<DataSourceFilter.Item>> & {
  onToggle: (dataSource: DataSourceFilter.Item, isSelected: boolean) => void;
  selectedIds: string[];
}) {
  const isSelected = props.selectedIds.includes(props.data.id);

  const handleCheckboxChange = useCallback(() => {
    props.onToggle(props.data, isSelected);
  }, [props, isSelected]);

  return (
    <ReactSelectComponents.Option {...props}>
      <input
        name={props.data.id}
        type="checkbox"
        checked={isSelected}
        onChange={handleCheckboxChange}
      />
      {props.label}
    </ReactSelectComponents.Option>
  );
}

const DataSourceContainer = styled.div({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  width: '100%',
});

const EmptyText = styled.div(({ theme }) => ({
  padding: '8px 12px',
  color: theme.palette.gray.main,
  fontSize: 14,
}));